import {
  Flex,
  Icon,
  MenuDivider,
  MenuItem,
  Tag,
  TagLeftIcon,
  Tooltip,
} from "@chakra-ui/react";
import { WarningIcon } from "@chakra-ui/icons";
import {
  MdAdminPanelSettings,
  MdComment,
  MdEmail,
  MdFolder,
  MdSettings,
} from "react-icons/md";
import { useApolloClient } from "@apollo/client";
import {
  useCurrentBrandData,
  useCurrentUserBrokers,
  useCurrentUserData,
  useIsInvalidSubscription,
} from "utils/currentUserHooks";
import SwitchAccountMenuItem from "components/Core/Auth/SwitchAccount/MenuItem";
import {
  ADMIN_INDEX,
  ANALYTICS_DATA_SOURCES,
  ANALYTICS_INDEX,
  BRAND_BILLING,
  BRAND_SETTINGS,
  COMMENTS_INDEX,
  DOCUMENTS_INDEX,
  EMAIL_INBOX_INDEX,
  PRODUCTS_INDEX,
} from "utils/links";
import { asyncVoid } from "utils/asyncVoidHandler";
import { APP_NAME } from "utils/config";
import {
  BRAND_LAYOUT_HOMEPAGES,
  useIsAnalyticsLayoutPath,
} from "components/Layout/brandLayoutUtils";
import TooltipIconLinkButton from "components/Layout/NavBar/TooltipIconLinkButton";
import { COMMENTS_TITLE } from "components/Comments/utils";
import BrandName from "components/Shared/BrandName";
import Link from "components/Shared/Link";
import NavBarContainer from "./Container";
import MenuItemLink from "./MenuItemLink";
import NavBarLogo from "./Logo";
import NavBarAccountMenu from "./AccountMenu";
import BrokerAccountMenuItem from "./BrokerAccountMenuItem";
import NavBarLink from "./NavBarLink";
import SideBarToggleButton from "./SideBarToggleButton";
import FeedbackButton from "./FeedbackButton";

type NavBarProps = {
  handleShowOrHideSidebar: () => void;
  isMobileSideBarHidden?: boolean;
  isMobileSideBarOpen: boolean;
};

export default function NavBar({
  handleShowOrHideSidebar,
  isMobileSideBarHidden,
  isMobileSideBarOpen,
}: NavBarProps) {
  const client = useApolloClient();
  const userData = useCurrentUserData();
  const brandData = useCurrentBrandData();
  const { brokers } = useCurrentUserBrokers();

  const isAnalyticsLayout = useIsAnalyticsLayoutPath();
  const isInvalidSubscription = useIsInvalidSubscription();

  return (
    <NavBarContainer id="NavBar">
      <NavBarLogo
        href={
          brandData?.layout
            ? BRAND_LAYOUT_HOMEPAGES[brandData.layout]
            : undefined
        }
      />

      {isInvalidSubscription && (
        <Tooltip
          hasArrow
          label={
            <>
              The subscription for <BrandName /> is invalid. Access to most
              features is disabled. Go to your subscription settings to learn
              more.
            </>
          }
        >
          <Link href={BRAND_BILLING} alignSelf="center">
            <Tag colorScheme="orange">
              <TagLeftIcon as={WarningIcon} />
              Invalid Subscription
            </Tag>
          </Link>
        </Tooltip>
      )}

      {isAnalyticsLayout && (
        <>
          <NavBarLink
            href={ANALYTICS_INDEX}
            label="Analytics"
            display={{ base: "none", md: "flex" }}
          />
          <NavBarLink
            href={ANALYTICS_DATA_SOURCES}
            label="Data Sources"
            exactPathname={false}
          />
          <NavBarLink href={PRODUCTS_INDEX} label="Products" />
        </>
      )}

      <Flex display={{ base: "none", md: "flex" }} marginLeft="auto">
        {isAnalyticsLayout === false && (
          <>
            <TooltipIconLinkButton
              href={COMMENTS_INDEX}
              label={COMMENTS_TITLE}
              icon={<Icon as={MdComment} />}
            />
            <TooltipIconLinkButton
              href={EMAIL_INBOX_INDEX}
              label="Email Inbox"
              icon={<Icon as={MdEmail} />}
            />
            <TooltipIconLinkButton
              href={DOCUMENTS_INDEX}
              label="Document Library"
              icon={<Icon as={MdFolder} />}
            />
            <FeedbackButton />
          </>
        )}

        <NavBarAccountMenu
          label={brandData?.name}
          image={userData?.image}
          isLoading={!userData}
        >
          <MenuItem
            as={MenuItemLink}
            href={BRAND_SETTINGS}
            icon={<MdSettings />}
          >
            Account Settings
          </MenuItem>
          <SwitchAccountMenuItem />

          {!!brokers?.length && (
            <>
              <MenuDivider />
              {brokers.map((broker) => (
                <BrokerAccountMenuItem
                  key={broker.id}
                  broker={broker}
                  onSelect={() => {
                    asyncVoid(client.clearStore());
                  }}
                />
              ))}
            </>
          )}

          {userData?.isStaff && (
            <>
              <MenuDivider />
              <MenuItem
                as={MenuItemLink}
                href={ADMIN_INDEX}
                icon={<MdAdminPanelSettings />}
                onClick={() => {
                  asyncVoid(client.clearStore());
                }}
              >
                {APP_NAME} Admin
              </MenuItem>
            </>
          )}
          <MenuDivider />
        </NavBarAccountMenu>
      </Flex>

      <SideBarToggleButton
        isMobileSideBarHidden={isMobileSideBarHidden}
        isMobileSideBarOpen={isMobileSideBarOpen}
        onToggle={handleShowOrHideSidebar}
        marginLeft="auto"
        marginRight={2}
      />
    </NavBarContainer>
  );
}
